'use client';
import React, { createContext, useContext, useState, useCallback } from 'react';

export type UserRole = 'applicant' | 'student' | 'teacher' | 'admin';

export interface RoleUser {
  name: string;
  email: string;
  initials: string;
  role: UserRole;
}

const roleUsers: Record<UserRole, RoleUser> = {
  applicant: { name: 'Jordan Lee', email: 'jlee@applicant.stargalms.edu', initials: 'JL', role: 'applicant' },
  student: { name: 'Luis Rivera', email: 'lrivera@student.stargalms.edu', initials: 'LR', role: 'student' },
  teacher: { name: 'Maria Santos', email: 'msantos@faculty.stargalms.edu', initials: 'MS', role: 'teacher' },
  admin: { name: 'Angela Johnson', email: 'ajohnson@admin.stargalms.edu', initials: 'AJ', role: 'admin' },
};

export const roleConfig: Record<UserRole, { label: string; color: string; bgColor: string; textColor: string; description: string }> = {
  applicant: {
    label: 'Applicant',
    color: 'orange',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    description: 'Prospective student — application in progress',
  },
  student: {
    label: 'Student',
    color: 'teal',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    description: 'Enrolled student with full course access',
  },
  teacher: {
    label: 'Teacher',
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    description: 'Instructor with class and grading access',
  },
  admin: {
    label: 'Administrator',
    color: 'purple',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    description: 'Full system access and management',
  },
};

interface RoleContextValue {
  role: UserRole;
  user: RoleUser;
  setRole: (role: UserRole) => void;
}

const RoleContext = createContext<RoleContextValue>({
  role: 'student',
  user: roleUsers['student'],
  setRole: () => {},
});

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const [role, setRoleState] = useState<UserRole>('student');

  const setRole = useCallback((newRole: UserRole) => {
    setRoleState(newRole);
  }, []);

  return (
    <RoleContext.Provider value={{ role, user: roleUsers[role], setRole }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}

export { roleUsers };
